import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';

import useSendTelemetry from 'logic/telemetry/useSendTelemetry';
import { TELEMETRY_EVENT_TYPE } from 'telemetry/Constants';
import { useEnableLatestPacks, useDisableLatestPacks, useGetPacksSummary } from 'illuminate/hooks/usePacksAPI';
import { useModalContext, useSelectedRows } from 'common/contexts';
import { ConfirmDialog } from 'common/components';
import type { Pack } from 'illuminate/types';
import EntityCreateShareFormGroup from 'components/permissions/EntityCreateShareFormGroup';
import type { EntitySharePayload } from 'actions/permissions/EntityShareActions';

import PacksList from './packs-list';

import { buildEnableDisablePayload } from '../../utils';

const CONST_MAP = {
  enable: {
    data: 'creation',
    title: 'Enable',
  },
  disable: {
    data: 'loss',
    title: 'Disable',
  },
};

function ConfEnableDisablePacks({ onCancel }: { onCancel: () => void }) {
  const { entity: actionType } = useModalContext();
  const selectedPacks = useSelectedRows();
  const { enablingPacks, enablePacks } = useEnableLatestPacks();
  const { disablingPacks, disablePacks } = useDisableLatestPacks();
  const [shareRequest, setShareRequest] = useState<EntitySharePayload | undefined>(undefined);
  const sendTelemetry = useSendTelemetry();
  const {
    packsSummary: { cached_version, current_version },
  } = useGetPacksSummary();
  const updatingPacks = React.useMemo(() => enablingPacks || disablingPacks, [enablingPacks, disablingPacks]);

  useEffect(() => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.ILLUMINATE[`PACK_${actionType.toUpperCase()}_MODAL_TOGGLED`], {
      app_pathname: 'illuminate',
      app_section: 'packs',
      event_details: {
        illuminate_enterprise: true,
        illuminate_version: '',
      },
    });
  }, [actionType, sendTelemetry]);

  const handleEnablingPacks = useCallback(async () => {
    const packsToEnable = selectedPacks.filter((pack: Pack) => !pack.enabled);
    sendTelemetry(TELEMETRY_EVENT_TYPE.ILLUMINATE.PACKS_BULK_UPDATED, {
      app_pathname: 'illuminate',
      app_section: 'packs',
      event_details: {
        illuminate_enterprise: true,
        illuminate_version: cached_version || current_version,
        illuminate_action: actionType,
        illuminate_packs: packsToEnable.map((pack: Pack) => ({
          id: pack.pack_id,
          title: pack.title,
          version: pack.version,
        })),
      },
    });

    const entity = buildEnableDisablePayload(packsToEnable);

    return enablePacks({ requestObject: { entity, share_request: shareRequest } });
  }, [actionType, cached_version, current_version, enablePacks, selectedPacks, sendTelemetry, shareRequest]);

  const handleDisablingPacks = useCallback(async () => {
    const packsToDisable = selectedPacks.filter((pack: Pack) => pack.enabled);
    sendTelemetry(TELEMETRY_EVENT_TYPE.ILLUMINATE.PACKS_BULK_UPDATED, {
      app_pathname: 'illuminate',
      app_section: 'packs',
      event_details: {
        illuminate_enterprise: true,
        illuminate_version: current_version,
        illuminate_action: actionType,
        illuminate_packs: packsToDisable.map((pack: Pack) => ({
          id: pack.pack_id,
          title: pack.title,
          version: pack.version,
        })),
      },
    });

    const requestObject = buildEnableDisablePayload(packsToDisable);

    return disablePacks({ requestObject });
  }, [actionType, current_version, disablePacks, selectedPacks, sendTelemetry]);

  const onConfirm = useCallback(async () => {
    if (actionType === 'enable') await handleEnablingPacks();
    if (actionType === 'disable') await handleDisablingPacks();
    onCancel();
  }, [actionType, handleDisablingPacks, handleEnablingPacks, onCancel]);

  const combinedAction =
    actionType +
    (actionType === 'enable' && selectedPacks.filter((pack: Pack) => pack.update_available).length > 0
      ? ' or update'
      : '');

  return (
    <ConfirmDialog
      show
      onConfirm={onConfirm}
      confirmDisabled={updatingPacks}
      isLoading={updatingPacks}
      confirmText="Confirm"
      onCancel={onCancel}
      title={`${CONST_MAP[actionType].title} Illuminate Pack${selectedPacks.length > 1 ? 's' : ''}`}>
      <p>
        You are about to <b>{combinedAction}</b> the packs listed below
      </p>
      <p>
        This could cause potential {CONST_MAP[actionType].data} of data and will pause all processing for a few seconds.
      </p>
      <p>Do you wish to continue?</p>
      <PacksList />
      {actionType === 'enable' && (
        <EntityCreateShareFormGroup
          entityType="Illuminate pack"
          description="Search for a User or Team to share the content of these Illuminate packs with:"
          entityTitle=""
          onSetEntityShare={setShareRequest}
        />
      )}
    </ConfirmDialog>
  );
}

export default ConfEnableDisablePacks;
